/**
 * `<kit-scope-nav>` — the ONE platform scope navigation.
 *
 * Every platform surface answers the same question in its top-left corner:
 * *where am I?* The answer is a hierarchy — space › app › section › deeper —
 * and at every level there are siblings you may switch to. This element is
 * that answer, rendered once for the whole platform:
 *
 *   [mark] │ career                       [mark] │ dna
 *          │ Workshops ⌄ › Mein Auftakt ⌄ › Werte ⌄     │ Bible ⌄ / Kapitel ⌄
 *
 * The composition is the operator's: the mark, then the APP NAME as an eyebrow
 * ABOVE the breadcrumb. Only the MARK changes when the application context
 * changes (see `marks.ts`); the chrome never does.
 *
 * The lower line is a REAL breadcrumb — an ordered list, section first, one
 * separator between neighbours, the deepest crumb marked as the current page —
 * and every crumb carries its OWN dropdown of the siblings at its level.
 *
 * Switching one of those siblings runs the cascading resolution rule
 * (`scope-resolution.ts`): the following crumbs are re-checked outermost-first,
 * survivors keep the person's place, the rest are dropped in order. Hand the
 * element a {@link ScopeResolver} through `resolveScope` and every
 * `kit-scope-select` carries the resolved outcome; without one, the chain
 * degrades to the switched level — the safe default.
 *
 * Feeding it (two seams, same validated model):
 *
 *   el.scope = { space: "personal", app: {…}, section: {…}, path: [ … ] }
 *   <kit-scope-nav scope='{"app":{"label":"Career"}}'></kit-scope-nav>
 *
 * The attribute seam exists for server-rendered and React hosts: a JSON string
 * survives SSR and hydration where an object property does not.
 *
 * Reacting to it: every choice dispatches `kit-scope-select`
 * (bubbles, composed, cancelable) carrying `{ level, id, url, label }`.
 * Anchors navigate natively unless a listener calls `preventDefault()` — which
 * is exactly what a client-side router does. A choice may ALSO carry the host
 * app's own delegated verb (`act` → `data-act`), so an app with one router
 * listener keeps it.
 *
 * Attributes (observed): `scope` (JSON), `space` (reflected from the model),
 * `nav-label` (accessible name of the nav), `content-lang` (the language of
 * user-authored segment labels).
 */

import { signal, type ReadonlySignal, type Signal } from "../core/signal";
import { KitElement } from "../core/element";
import { defineElement } from "../core/define";
import { defineBrandMark } from "../brand/kit-brand-mark";
import { isSpace, type Space } from "../brand/spaces";
import {
  html,
  nothing,
  repeat,
  classMap,
  ifDefined,
  type TemplateResult,
} from "../render/html";
import type { ViewModel } from "../core/viewmodel";
import {
  parseScopeJson,
  parseScope,
  pathSegments,
  scopeLabel,
  scopeChain,
  chainIndexOf,
  segmentAt,
  levelKey,
  type LeveledSegment,
  type ScopeChoice,
  type ScopeLevel,
  type ScopeModel,
  type ScopeSegment,
} from "./scope-model";
import {
  resolveScopeSwitch,
  type ScopeNode,
  type ScopeResolver,
  type ScopeSwitchResult,
} from "./scope-resolution";
import { resolveScopeMark } from "./marks";
import { installScopeNavStyles } from "./scope-styles";

/** What `kit-scope-select` carries. */
export interface ScopeSelectDetail {
  /** Which level of the hierarchy the choice belongs to. */
  level: ScopeLevel;
  /** Index within the level (0 unless it is a `path` segment). */
  index: number;
  /** The chosen sibling's stable id, when it has one. */
  id?: string;
  /** The chosen sibling's target, when it is navigable. */
  url?: string;
  /** The chosen sibling's display text. */
  label: string;
  /**
   * The cascading outcome — present only when the host set `resolveScope`.
   * `resolution.url` is the route to take: the deepest crumb that SURVIVED the
   * switch, which is at least as deep as the chosen sibling itself.
   */
  resolution?: ScopeSwitchResult;
}

/** The event name every host listens to. */
export const SCOPE_SELECT_EVENT = "kit-scope-select";

class ScopeNavVm implements ViewModel {
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>;

  constructor(
    readonly scope: Signal<ScopeModel | null>,
    readonly navLabel: Signal<string>,
    readonly contentLang: Signal<string | null>
  ) {
    this.renderDeps = [scope, navLabel, contentLang];
  }
}

let instanceCounter = 0;

const CARET = html`<svg class="scope-caret" viewBox="0 0 24 24" aria-hidden="true">
  <path d="m6 9 6 6 6-6" />
</svg>`;

export class KitScopeNav extends KitElement<ScopeNavVm> {
  static override readonly observedAttributes: readonly string[] = [
    ...KitElement.observedAttributes,
    "scope",
    "space",
    "nav-label",
    "content-lang",
  ];

  readonly #scope = signal<ScopeModel | null>(null);
  readonly #navLabel = signal("Scope");
  readonly #contentLang = signal<string | null>(null);
  readonly #uid = `kit-scope-${++instanceCounter}`;
  #reflecting = false;

  /**
   * The host's answer to »does this crumb still resolve under the new context?«.
   * Set it and every selection carries `detail.resolution`; leave it and the
   * chain degrades to the switched level (see `scope-resolution.ts`).
   */
  resolveScope: ScopeResolver | null = null;

  /** The validated scope model. Setting an invalid one throws `ScopeModelError`. */
  get scope(): ScopeModel | null {
    return this.#scope.value;
  }

  set scope(model: ScopeModel | null) {
    if (model === null) {
      this.#scope.set(null);
      this.#reflectSpace(null);
      return;
    }
    const parsed = parseScope(model);
    this.#scope.set(parsed);
    this.#reflectSpace(parsed.space ?? null);
  }

  /** The top scope. Reflected as an attribute so CSS can carry the accent. */
  get space(): Space | null {
    const value = this.getAttribute("space");
    return isSpace(value) ? value : null;
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;
    switch (name) {
      case "scope": {
        if (newValue === null) {
          this.#scope.set(null);
          break;
        }
        const parsed = parseScopeJson(newValue);
        this.#scope.set(parsed);
        this.#reflectSpace(parsed.space ?? null);
        break;
      }
      case "space": {
        // Host-set space overrides the model's — the model is re-stamped so the
        // two can never disagree in the rendered output.
        const current = this.#scope.value;
        if (this.#reflecting || !current) break;
        this.#scope.set({ ...current, space: isSpace(newValue) ? newValue : null });
        break;
      }
      case "nav-label":
        this.#navLabel.set(newValue ?? "Scope");
        break;
      case "content-lang":
        this.#contentLang.set(newValue);
        break;
      default:
        break;
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("click", this.#onClick);
    this.addEventListener("keydown", this.#onKeydown);
    // Popover toggle events do NOT bubble — a capturing listener still sees them.
    this.addEventListener("beforetoggle", this.#onBeforeToggle, true);
    this.addEventListener("toggle", this.#onToggle, true);
    // `scroll` does not bubble — a capturing listener still sees the crumb row's.
    this.addEventListener("scroll", this.#markOverflow, true);
  }

  override disconnectedCallback(): void {
    this.removeEventListener("click", this.#onClick);
    this.removeEventListener("keydown", this.#onKeydown);
    this.removeEventListener("beforetoggle", this.#onBeforeToggle, true);
    this.removeEventListener("toggle", this.#onToggle, true);
    this.removeEventListener("scroll", this.#markOverflow, true);
    super.disconnectedCallback();
  }

  protected createViewModel(): ScopeNavVm {
    return new ScopeNavVm(this.#scope, this.#navLabel, this.#contentLang);
  }

  #reflectSpace(space: Space | null): void {
    this.#reflecting = true;
    if (space === null) this.removeAttribute("space");
    else if (this.getAttribute("space") !== space)
      this.setAttribute("space", space);
    this.#reflecting = false;
  }

  /** A choice was activated: tell the host, then let the anchor do its job. */
  readonly #onClick = (event: Event): void => {
    const target = event.target as Element | null;
    const choice = target?.closest?.(".scope-choice") as HTMLElement | null;
    if (!choice || !this.contains(choice)) return;
    const level = (choice.dataset.scopeLevel ?? "path") as ScopeLevel;
    const index = Number(choice.dataset.scopeIndex ?? "0");
    const label = choice.dataset.scopeLabel ?? choice.textContent?.trim() ?? "";
    const url =
      choice instanceof HTMLAnchorElement && choice.dataset.scopeUrl
        ? choice.dataset.scopeUrl
        : undefined;
    const detail: ScopeSelectDetail = {
      level,
      index,
      label,
      ...(choice.dataset.scopeId ? { id: choice.dataset.scopeId } : {}),
      ...(url !== undefined ? { url } : {}),
      ...(this.#resolution(level, index, {
        label,
        ...(choice.dataset.scopeId ? { id: choice.dataset.scopeId } : {}),
        ...(url !== undefined ? { url } : {}),
      }) ?? {}),
    };
    const proceed = this.dispatchEvent(
      new CustomEvent<ScopeSelectDetail>(SCOPE_SELECT_EVENT, {
        detail,
        bubbles: true,
        composed: true,
        cancelable: true,
      })
    );
    // A router that handled the selection cancels the event; the browser must
    // then NOT follow the href as well.
    if (!proceed) event.preventDefault();
    closeMenu(choice.closest<HTMLElement>(".scope-menu"));
  };

  /**
   * Run the cascading rule for a selection — `{ resolution }` to spread into the
   * event detail, or `null` when the host has no resolver (nothing to cascade
   * with) or the model no longer carries that level.
   */
  #resolution(
    level: ScopeLevel,
    index: number,
    choice: ScopeChoice
  ): { resolution: ScopeSwitchResult } | null {
    const model = this.#scope.value;
    const resolve = this.resolveScope;
    if (!model || resolve === null) return null;
    const origin = chainIndexOf(model, level, index);
    if (origin < 0) return null;
    const key = segmentAt(model, level, index)?.key ?? levelKey(level, index);
    const node: ScopeNode = {
      key,
      id: choice.id ?? choice.url ?? choice.label,
      label: choice.label,
      ...(choice.url !== undefined ? { url: choice.url } : {}),
    };
    return {
      resolution: resolveScopeSwitch({
        chain: scopeChain(model),
        index: origin,
        choice: node,
        resolve,
      }),
    };
  }

  /** Roving focus inside an open menu (Escape and Tab are native). */
  readonly #onKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const target = event.target as Element | null;
    const menu = target?.closest?.(".scope-menu") as HTMLElement | null;
    if (!menu) return;
    const items = [...menu.querySelectorAll<HTMLElement>(".scope-choice")];
    if (items.length === 0) return;
    const at = items.findIndex((item) => item === target);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next = items[(at + step + items.length) % items.length];
    next?.focus();
    event.preventDefault();
  };

  readonly #onBeforeToggle = (event: Event): void => {
    const menu = event.target as HTMLElement | null;
    if (!menu?.classList?.contains("scope-menu")) return;
    const open = toggleState(event) === "open";
    const trigger = this.querySelector<HTMLElement>(
      `[popovertarget="${menu.id}"]`
    );
    trigger?.setAttribute("aria-expanded", open ? "true" : "false");
  };

  /** Position fallback for engines without CSS anchor positioning. */
  readonly #onToggle = (event: Event): void => {
    const menu = event.target as HTMLElement | null;
    if (!menu?.classList?.contains("scope-menu")) return;
    if (toggleState(event) !== "open") return;
    if (typeof CSS !== "undefined" && CSS.supports?.("anchor-name: --a")) return;
    const trigger = this.querySelector<HTMLElement>(
      `[popovertarget="${menu.id}"]`
    );
    if (!trigger) return;
    const anchor = trigger.getBoundingClientRect();
    const box = menu.getBoundingClientRect();
    const gutter = 12;
    const viewportWidth = globalThis.innerWidth || box.width + anchor.left;
    const viewportHeight = globalThis.innerHeight || box.height + anchor.bottom;
    const left = Math.max(
      gutter,
      Math.min(anchor.left, viewportWidth - box.width - gutter)
    );
    const below = anchor.bottom + 6;
    const top =
      below + box.height > viewportHeight - gutter
        ? Math.max(gutter, anchor.top - box.height - 6)
        : below;
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  };

  /**
   * Say which SIDE of the breadcrumb continues past the edge — `data-overflow`
   * is `none | start | end | both`, and the stylesheet fades that edge. Without
   * it a crumb cut off at the inline start reads as a rendering fault instead of
   * »there is more this way«.
   */
  readonly #markOverflow = (): void => {
    const crumbs = this.querySelector<HTMLElement>(".scope-crumbs");
    if (!crumbs) return;
    const { scrollLeft, scrollWidth, clientWidth } = crumbs;
    const start = scrollLeft > 1;
    const end = scrollLeft + clientWidth < scrollWidth - 1;
    crumbs.dataset.overflow =
      start && end ? "both" : start ? "start" : end ? "end" : "none";
  };

  #menuId(level: ScopeLevel, index: number): string {
    return `${this.#uid}-${level}-${index}`;
  }

  /** One choice: a real anchor when navigable, a real button otherwise. */
  #choice(
    choice: ScopeChoice,
    level: ScopeLevel,
    index: number,
    lang: string | null
  ): TemplateResult {
    if (choice.heading) {
      return html`<span class="scope-menu-group" role="presentation" lang=${ifDefined(lang ?? undefined)}
        >${choice.label}</span
      >`;
    }
    const body = html`<span class="scope-choice-label">${choice.label}</span>${choice.hint
      ? html`<span class="scope-choice-hint">${choice.hint}</span>`
      : nothing}`;
    const classes = classMap({ "scope-choice": true, "is-current": choice.current === true });
    const data = choice.data ?? {};
    if (choice.url !== undefined) {
      return html`<a
        class=${classes}
        role="menuitem"
        href=${choice.url}
        data-act=${ifDefined(choice.act)}
        data-scope-level=${level}
        data-scope-index=${String(index)}
        data-scope-label=${choice.label}
        data-scope-url=${choice.url}
        data-scope-id=${ifDefined(choice.id)}
        data-scope-payload=${ifDefined(serializePayload(data))}
        aria-current=${ifDefined(choice.current ? "true" : undefined)}
        lang=${ifDefined(lang ?? undefined)}
        >${body}</a
      >`;
    }
    return html`<button
      type="button"
      class=${classes}
      role="menuitem"
      data-act=${ifDefined(choice.act)}
      data-scope-level=${level}
      data-scope-index=${String(index)}
      data-scope-label=${choice.label}
      data-scope-id=${ifDefined(choice.id)}
      data-scope-payload=${ifDefined(serializePayload(data))}
      aria-current=${ifDefined(choice.current ? "true" : undefined)}
      lang=${ifDefined(lang ?? undefined)}
    >
      ${body}
    </button>`;
  }

  /** One segment: static text, or a dropdown trigger plus its popover menu. */
  #segment(
    segment: ScopeSegment,
    level: ScopeLevel,
    index: number,
    lang: string | null,
    leaf = false
  ): TemplateResult {
    const label = segment.content === true ? lang : null;
    const crumbClasses = classMap({
      "scope-crumb": true,
      "scope-crumb-static": !segment.choices || segment.choices.length === 0,
      "scope-crumb-leaf": leaf,
    });
    if (!segment.choices || segment.choices.length === 0) {
      return html`<span
        class=${crumbClasses}
        aria-current=${ifDefined(leaf ? "page" : undefined)}
        lang=${ifDefined(label ?? undefined)}
        >${segment.label}</span
      >`;
    }
    const id = this.#menuId(level, index);
    return html`<button
        type="button"
        class=${crumbClasses}
        popovertarget=${id}
        aria-expanded="false"
        aria-haspopup="menu"
        aria-current=${ifDefined(leaf ? "page" : undefined)}
        aria-label=${ifDefined(segment.menuLabel)}
      >
        <span class="scope-crumb-text" lang=${ifDefined(label ?? undefined)}
          >${segment.label}</span
        >${CARET}
      </button>
      <div class="scope-menu" popover="auto" id=${id} role="menu" aria-label=${ifDefined(segment.menuLabel)}>
        ${repeat(
          segment.choices,
          (choice, i) => `${i}:${choice.id ?? choice.url ?? choice.label}`,
          (choice) => this.#choice(choice, level, index, lang)
        )}
      </div>`;
  }

  /**
   * Render, then (a) park the breadcrumb on the crumb you are actually in and
   * (b) expand each choice's host-verb payload onto its node.
   *
   * (a): on a bar too narrow for the whole trail the row scrolls rather than
   * squeezing its crumbs into initials. The deepest crumb is the answer to
   * »where am I«, so that is the end the row rests at; the ancestors are one
   * swipe away and never left the DOM. Skipped while the person is scrolling or
   * focused inside — the element must never fight the hand.
   */
  override flushRender(): void {
    super.flushRender();
    const crumbs = this.querySelector<HTMLElement>(".scope-crumbs");
    if (crumbs && !crumbs.contains(this.ownerDocument?.activeElement ?? null)) {
      crumbs.scrollLeft = crumbs.scrollWidth;
    }
    this.#markOverflow();
    for (const node of this.querySelectorAll<HTMLElement>(
      ".scope-choice[data-scope-payload]"
    )) {
      const raw = node.dataset.scopePayload;
      if (raw === undefined) continue;
      delete node.dataset.scopePayload;
      let payload: unknown;
      try {
        payload = JSON.parse(raw);
      } catch {
        continue;
      }
      if (typeof payload !== "object" || payload === null) continue;
      for (const [key, value] of Object.entries(payload)) {
        // AX-9: structural vocabulary is ASCII kebab-case English.
        if (!DATA_KEY_RE.test(key) || typeof value !== "string") continue;
        node.setAttribute(`data-${key}`, value);
      }
    }
  }

  protected render(vm: ScopeNavVm): TemplateResult {
    const model = vm.scope.value;
    const lang = vm.contentLang.value;
    if (!model) return html`${nothing}`;
    const space = model.space ?? null;
    const section = model.section?.label ?? null;
    const mark = resolveScopeMark(model.mark)({
      app: model.app.label,
      space,
      section,
    });
    // THE breadcrumb: the section, then every deeper segment — one ordered list,
    // one line. The app name is NOT a crumb; it is the eyebrow above the trail.
    const crumbs: LeveledSegment[] = [
      ...(model.section ? [{ level: "section" as const, index: 0, segment: model.section }] : []),
      ...pathSegments(model),
    ];
    const last = crumbs.length - 1;
    const home =
      model.homeUrl !== undefined
        ? html`<a
            class="scope-home"
            href=${model.homeUrl}
            aria-label=${model.app.label}
            data-scope-level="app"
            >${mark}</a
          >`
        : html`<span class="scope-home">${mark}</span>`;
    return html`${home}
      <span class="scope-rule" aria-hidden="true"></span>
      <span class="scope-lockup">
        <span class="scope-line scope-line-app">
          ${model.spaceSegment
            ? html`${this.#segment(model.spaceSegment, "space", 0, lang)}<span
                  class="scope-sep"
                  aria-hidden="true"
                ></span>`
            : nothing}${this.#segment(model.app, "app", 0, lang)}
        </span>
        ${crumbs.length > 0
          ? html`<nav class="scope-line scope-path" aria-label=${vm.navLabel.value}>
              <ol class="scope-crumbs">
                ${repeat(
                  crumbs,
                  ({ level, index }) => `${level}:${index}`,
                  ({ level, index, segment }, i) =>
                    html`<li
                      class=${classMap({
                        "scope-crumb-item": true,
                        [`scope-crumb-${level}`]: true,
                        "is-leaf": i === last,
                      })}
                    >
                      ${i > 0
                        ? html`<span class="scope-sep" aria-hidden="true"></span>`
                        : nothing}${this.#segment(segment, level, index, lang, i === last)}
                    </li>`
                )}
              </ol>
            </nav>`
          : nothing}
      </span>
      <span class="scope-full" hidden>${scopeLabel(model)}</span>`;
  }
}

const DATA_KEY_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The host verb's `data-*` payload rides ONE attribute through the template
 * (lit has no sanctioned attribute-spread) and is expanded onto the node after
 * the render — so a host delegate reads `data-url` exactly as it would on its
 * own markup.
 */
function serializePayload(data: Record<string, string>): string | undefined {
  const keys = Object.keys(data);
  return keys.length > 0 ? JSON.stringify(data) : undefined;
}

/** `ToggleEvent.newState` without depending on the lib.dom vintage. */
function toggleState(event: Event): string | undefined {
  const { newState } = event as Event & { newState?: string };
  return newState;
}

/** Close a popover menu defensively — engines throw when it is not showing. */
function closeMenu(menu: HTMLElement | null): void {
  if (!menu || typeof menu.hidePopover !== "function") return;
  try {
    menu.hidePopover();
  } catch {
    /* not open (or no popover support) — nothing to close */
  }
}

/** Register `<kit-scope-nav>` + its stylesheet + the default brand composition. */
export function defineScopeNav(): void {
  installScopeNavStyles();
  defineBrandMark();
  defineElement("kit-scope-nav", KitScopeNav);
}
